import { useState, useEffect, useMemo, ReactNode, CSSProperties, Ref } from 'react';
import { createPortal } from 'react-dom';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import classnames from '../util/classnames';
import { getTestId } from '../hooks/useTestId';

// ─── Types ────────────────────────────────────────────────────────────────────

export type PanelMode = 'right' | 'bottom' | 'float' | 'popout';
export type ToolbarColor = 'primary' | 'danger' | 'warning';

export type TabDef = {
  mainContent: string;
  title: string;
  type?: string;
  [key: string]: any;
};

// ─── Utilities ────────────────────────────────────────────────────────────────

export function getToolbarColor(tab?: TabDef | null): ToolbarColor {
  if (tab?.type === 'conflicts') return 'warning';
  return tab?.mainContent === 'dataremarks' ? 'danger' : 'primary';
}

// ─── Icons ────────────────────────────────────────────────────────────────────

export function IconRight() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
      <rect x="1" y="1" width="14" height="14" rx="2" />
      <line x1="8" y1="1" x2="8" y2="15" />
    </svg>
  );
}

export function IconBottom() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
      <rect x="1" y="1" width="14" height="14" rx="2" />
      <line x1="1" y1="9" x2="15" y2="9" />
    </svg>
  );
}

export function IconFloat() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
      <rect x="2" y="2" width="12" height="12" rx="2" />
      <polyline points="5,2 2,2 2,5" />
      <polyline points="11,2 14,2 14,5" />
    </svg>
  );
}

export function IconClose() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" width="14" height="14">
      <line x1="3" y1="3" x2="13" y2="13" />
      <line x1="13" y1="3" x2="3" y2="13" />
    </svg>
  );
}

export function IconPopout() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="14" height="14">
      <path d="M7 2H2a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V9" />
      <path d="M10 1h5v5" />
      <line x1="15" y1="1" x2="7" y2="9" />
    </svg>
  );
}

// ─── PanelHeader ──────────────────────────────────────────────────────────────

type PanelHeaderProps = {
  title: string;
  panelMode: PanelMode;
  onSetMode: (mode: PanelMode) => void;
  onClose: () => void;
  toolbarColor?: ToolbarColor;
  headerRef?: Ref<HTMLDivElement>;
  style?: CSSProperties;
  hideModeSwitchers?: boolean;
};

export function PanelHeader({ title, panelMode, onSetMode, onClose, toolbarColor = 'primary', headerRef, style, hideModeSwitchers }: PanelHeaderProps) {
  return (
    <div ref={headerRef} className={`sp-panel-hdr color-${toolbarColor}`} style={style}>
      <span className="sp-panel-hdr-title">{title}</span>
      <div className="sp-panel-hdr-actions">
        {!hideModeSwitchers && (
          <>
            <button
              className={classnames('sp-icon-btn', { 'sp-icon-btn-active': panelMode === 'right' })}
              onClick={() => onSetMode('right')}
              title="Split derecha"
              data-sp-btn="true"
              {...getTestId('sidebar-panel', '.', 'split-right')}
            >
              <IconRight />
            </button>
            <button
              className={classnames('sp-icon-btn', { 'sp-icon-btn-active': panelMode === 'bottom' })}
              onClick={() => onSetMode('bottom')}
              title="Split abajo"
              data-sp-btn="true"
              {...getTestId('sidebar-panel', '.', 'split-bottom')}
            >
              <IconBottom />
            </button>
            <button
              className={classnames('sp-icon-btn', { 'sp-icon-btn-active': panelMode === 'float' })}
              onClick={() => onSetMode('float')}
              title="Modal flotante"
              data-sp-btn="true"
              {...getTestId('sidebar-panel', '.', 'float')}
            >
              <IconFloat />
            </button>
            <button
              className={classnames('sp-icon-btn', { 'sp-icon-btn-active': panelMode === 'popout' })}
              onClick={() => onSetMode('popout')}
              title="Ventana separada"
              data-sp-btn="true"
              {...getTestId('sidebar-panel', '.', 'popout')}
            >
              <IconPopout />
            </button>
          </>
        )}
        <button className="sp-icon-btn" onClick={onClose} title="Cerrar" data-sp-btn="true" {...getTestId('sidebar-panel', '.', 'close')}>
          <IconClose />
        </button>
      </div>
    </div>
  );
}

// ─── PopoutWindow ─────────────────────────────────────────────────────────────

type PopoutWindowProps = {
  title: string;
  panelMode: PanelMode;
  onSetMode: (mode: PanelMode) => void;
  onClose: () => void;
  toolbarColor: ToolbarColor;
  tabs?: TabDef[];
  activeTab?: string | null;
  onTabChange?: (mainContent: string) => void;
  onWindowReady?: (win: Window) => void;
  globalStyles?: ReactNode;
  hideModeSwitchers?: boolean;
  children: ReactNode;
};

export default function PopoutWindow({
  title,
  panelMode,
  onSetMode,
  onClose,
  toolbarColor,
  tabs,
  activeTab,
  onTabChange,
  onWindowReady,
  globalStyles,
  hideModeSwitchers,
  children,
}: PopoutWindowProps) {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  const [popupHead, setPopupHead] = useState<HTMLHeadElement | null>(null);

  const emotionCache = useMemo(() => {
    if (!popupHead) return null;
    return createCache({ key: 'css', container: popupHead });
  }, [popupHead]);

  useEffect(() => {
    // Use a real URL rather than about:blank. Chrome applies strict Mojo IPC
    // validation to about:blank popup navigation commits and, depending on the
    // renderer process model, can kill the renderer with RESULT_CODE_KILLED_BAD_MESSAGE.
    // A real same-origin URL goes through the normal navigation path and avoids this.
    const popupUrl = new URL('/popup-frame.html', document.baseURI).href;
    const win = window.open(popupUrl, '', 'width=680,height=540,resizable=yes,scrollbars=yes');
    if (!win) {
      onClose();
      return;
    }

    // These are set once setup() runs after the popup page loads.
    // Declared here so the cleanup closure can always reach them.
    let checkClosed: ReturnType<typeof setInterval> | null = null;
    let titleObserver: MutationObserver | null = null;
    let headObserver: MutationObserver | null = null;
    let bodyObserver: MutationObserver | null = null;
    const styleObservers: MutationObserver[] = [];

    const handleUnload = () => {
      if (win && !win.closed) win.close();
    };
    window.addEventListener('beforeunload', handleUnload);

    const setup = () => {
      if (win.closed) {
        onClose();
        return;
      }

      // Match the main window's title and keep it in sync
      win.document.title = document.title;
      const titleEl = document.querySelector('title');
      titleObserver = titleEl
        ? new MutationObserver(() => {
            if (win.closed) return;
            win.document.title = document.title;
          })
        : null;
      titleObserver?.observe(titleEl!, { childList: true, characterData: true, subtree: true });

      win.document.body.style.margin = '0';
      win.document.body.className = document.body.className;
      win.document.body.dataset.theme = document.body.dataset.theme ?? 'light';

      // Track source→clone pairs so we can keep style text content in sync
      const nodeMap = new Map<Element, Element>();

      function watchStyleContent(srcStyle: Element) {
        const obs = new MutationObserver(() => {
          if (!win || win.closed) return;
          const clone = nodeMap.get(srcStyle);
          if (clone && clone.textContent !== srcStyle.textContent) {
            clone.textContent = srcStyle.textContent;
          }
        });
        obs.observe(srcStyle, { childList: true, characterData: true, subtree: true });
        styleObservers.push(obs);
      }

      function buildPopupNode(src: Element): Element | null {
        // Create elements directly in the popup's document rather than cloning from the
        // main document and adopting cross-document. Cross-document node adoption requires
        // Chrome to serialize the entire node subtree via IPC, which can trigger
        // RESULT_CODE_KILLED_BAD_MESSAGE when the payload is large (e.g. Tailwind <style>
        // blocks) or contains attributes Chrome's IPC validator rejects.
        if (src.tagName === 'STYLE') {
          const el = win!.document.createElement('style');
          Array.from(src.attributes).forEach(attr => {
            try { el.setAttribute(attr.name, attr.value); } catch { /* skip invalid attrs */ }
          });
          el.textContent = src.textContent;
          return el;
        }

        if (src.tagName === 'LINK') {
          const el = win!.document.createElement('link');
          Array.from(src.attributes).forEach(attr => {
            if (attr.name.toLowerCase() === 'crossorigin') return;
            try { el.setAttribute(attr.name, attr.value); } catch { /* skip invalid attrs */ }
          });
          // Use the browser-resolved absolute URL so relative paths work from popup-frame.html.
          if ((src as HTMLLinkElement).href) el.href = (src as HTMLLinkElement).href;
          const rel = el.rel?.toLowerCase();
          // Skip modulepreload/preload[as=script] — popup can't execute JS modules.
          if (rel === 'modulepreload' || (rel === 'preload' && el.getAttribute('as') === 'script')) return null;
          return el;
        }

        // META, BASE, and other small attribute-only elements: clone normally.
        const clone = src.cloneNode(true) as Element;
        clone.removeAttribute('crossorigin');
        return clone;
      }

      function syncNode(src: Element) {
        if (nodeMap.has(src)) return;
        const clone = buildPopupNode(src);
        if (!clone) return;
        nodeMap.set(src, clone);
        win!.document.head.appendChild(clone);
        if (src.tagName === 'STYLE') watchStyleContent(src);
      }

      // Copy the entire <head> except scripts (scripts must not re-execute in the popup)
      document.head.childNodes.forEach(node => {
        if (node.nodeType === 1 && (node as Element).tagName !== 'SCRIPT') {
          syncNode(node as Element);
        }
      });

      // Pick up any non-script nodes injected into <head> after the window opens
      headObserver = new MutationObserver(mutations => {
        if (win.closed) return;
        for (const m of mutations) {
          m.addedNodes.forEach(node => {
            if (node.nodeType !== 1) return;
            const el = node as Element;
            if (el.tagName !== 'SCRIPT') syncNode(el);
          });
        }
      });
      headObserver.observe(document.head, { childList: true });

      // Keep body class and data-theme in sync when dark mode is toggled while popup is open
      bodyObserver = new MutationObserver(() => {
        if (win.closed) return;
        win.document.body.className = document.body.className;
        win.document.body.dataset.theme = document.body.dataset.theme ?? 'light';
      });
      bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-theme'] });

      const div = win.document.createElement('div');
      div.style.cssText = 'height:100vh;width:100%;display:flex;flex-direction:column;overflow:hidden;';
      win.document.body.appendChild(div);
      setPopupHead(win.document.head);
      setContainer(div);
      onWindowReady?.(win);

      checkClosed = setInterval(() => {
        if (win.closed) {
          clearInterval(checkClosed!);
          checkClosed = null;
          setContainer(null);
          setPopupHead(null);
          onClose();
        }
      }, 100);
    };

    // Always wait for the load event. window.open(url) creates an initial
    // about:blank document (readyState 'complete') before navigating to the
    // target URL, so checking readyState here would fire setup() on the wrong
    // transient document — popup-frame.html would then replace it and wipe
    // everything we wrote.
    win.addEventListener('load', setup);

    return () => {
      win.removeEventListener('load', setup);
      // Stop the portal from rendering before closing the window so React
      // doesn't attempt to commit DOM updates into a destroyed document.
      setContainer(null);
      setPopupHead(null);
      if (checkClosed != null) clearInterval(checkClosed);
      titleObserver?.disconnect();
      headObserver?.disconnect();
      bodyObserver?.disconnect();
      styleObservers.forEach(obs => obs.disconnect());
      window.removeEventListener('beforeunload', handleUnload);
      handleUnload();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!container || !emotionCache) return null;

  return createPortal(
    <CacheProvider value={emotionCache}>
      {globalStyles}
      {tabs && tabs.length > 0 && (
        <div style={{ display: 'flex', flexShrink: 0, overflowX: 'auto', borderBottom: '1px solid #dee2e6' }}>
          {tabs.map((tab, idx) => {
            const color = getToolbarColor(tab);
            const isActive = activeTab === tab.mainContent;
            return (
              <button
                key={idx}
                className={classnames('sp-side-tab', `tab-${color}`, { [`active-${color}`]: isActive })}
                onClick={() => onTabChange?.(tab.mainContent)}
                style={{ writingMode: 'horizontal-tb', transform: 'none', padding: '6px 14px', borderBottom: 'none', flexGrow: 1 }}
                {...getTestId('sidebar', '.', 'popout-tab', '-', tab.mainContent)}
              >
                {tab.title}
              </button>
            );
          })}
        </div>
      )}
      <PanelHeader title={title} panelMode={panelMode} onSetMode={onSetMode} onClose={onClose} toolbarColor={toolbarColor} hideModeSwitchers={hideModeSwitchers} />
      <div className="sp-panel-content">{children}</div>
    </CacheProvider>,
    container
  );
}
